import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import AdminPage from '../../components/admin/AdminPage';
import StatusChip from '../../components/admin/StatusChip';
import MetricCard from '../../components/admin/MetricCard';
import notificationsService, {
  BatchAnalytics,
  ScheduledNotification,
} from '../../services/NotificationsService';

const NotificationDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [row, setRow] = useState<ScheduledNotification | null>(null);
  const [analytics, setAnalytics] = useState<BatchAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [detail, stats] = await Promise.all([
        notificationsService.getScheduled(id),
        notificationsService.batchAnalytics(id),
      ]);
      setRow(detail);
      setAnalytics(stats);
    } catch {
      setError('Failed to load notification.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCancel = async () => {
    if (!id) return;
    setCancelling(true);
    setError(null);
    setNotice(null);
    try {
      await notificationsService.cancelScheduled(id);
      setNotice('Cancelled.');
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Cancel failed.');
    } finally {
      setCancelling(false);
    }
  };

  if (loading || !row) {
    return (
      <AdminPage title="Notification">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </AdminPage>
    );
  }

  const canCancel = row.status === 'DRAFT' || row.status === 'SCHEDULED';

  return (
    <AdminPage
      title={row.titleEn}
      description={`Category: ${row.category} · Audience: ${row.audienceKind}`}
      action={
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate('/notifications')}>
            Back to list
          </Button>
          {canCancel ? (
            <Button
              variant="contained"
              color="error"
              disabled={cancelling}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          ) : null}
        </Stack>
      }
    >
      {error ? <Alert severity="error">{error}</Alert> : null}
      {notice ? <Alert severity="success">{notice}</Alert> : null}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <MetricCard label="Status" value={<StatusChip value={row.status} />} />
        </Grid>
        <Grid item xs={6} md={4}>
          <MetricCard
            label="Scheduled for"
            value={new Date(row.scheduledFor).toLocaleString()}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <MetricCard
            label="Recipients"
            value={
              row.totalRecipients != null
                ? row.totalRecipients.toLocaleString()
                : '—'
            }
          />
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Content
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="overline" color="text.secondary">
                  English
                </Typography>
                <Typography variant="subtitle1">{row.titleEn}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {row.bodyEn}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{ p: 2, borderRadius: 2, direction: 'rtl' }}
              >
                <Typography variant="overline" color="text.secondary">
                  Arabic
                </Typography>
                <Typography variant="subtitle1">{row.titleAr}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {row.bodyAr}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          {row.deepLink ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Deep link
              </Typography>
              <Typography variant="body2">{row.deepLink}</Typography>
            </>
          ) : null}
        </CardContent>
      </Card>

      {analytics ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Delivery & engagement
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <MetricCard label="Sent" value={analytics.totals.sent} />
              </Grid>
              <Grid item xs={6} md={3}>
                <MetricCard label="Failed" value={analytics.totals.failed} />
              </Grid>
              <Grid item xs={6} md={3}>
                <MetricCard
                  label="Suppressed"
                  value={analytics.totals.suppressed}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <MetricCard
                  label="Delivered (receipts)"
                  value={analytics.deliveries.delivered}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <MetricCard label="Opened" value={analytics.events.opened} />
              </Grid>
              <Grid item xs={6} md={3}>
                <MetricCard label="Clicked" value={analytics.events.clicked} />
              </Grid>
              <Grid item xs={6} md={3}>
                <MetricCard
                  label="Open rate"
                  value={formatRate(analytics.openRate)}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <MetricCard
                  label="CTR"
                  value={formatRate(analytics.clickThroughRate)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ) : null}

      {row.failureReason ? (
        <Alert severity="error">Failure reason: {row.failureReason}</Alert>
      ) : null}
    </AdminPage>
  );
};

const formatRate = (rate: number | null): string =>
  rate == null ? '—' : `${(rate * 100).toFixed(1)}%`;

export default NotificationDetailsPage;
